import React, { useCallback, useRef, useState } from 'react';
import { IconMoreVertical } from '../../assets/icons/IconMoreVertical';
import type { ActionMenuProps } from './types';
import { useMenuDismiss } from './useMenuDismiss';
import { useMenuKeyboard } from './useMenuKeyboard';

/** A "⋮" button opening a short list of actions under it, right-aligned to the button; greyed out with no actions. */
export const ActionMenu: React.FC<ActionMenuProps> = ({ items, ariaLabel, testId }) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const close = useCallback(() => setOpen(false), []);

    useMenuDismiss(open, rootRef, close);
    const handleMenuKeyDown = useMenuKeyboard(open, menuRef, triggerRef, close);

    return (
        <div ref={rootRef} className="relative flex">
            <button
                ref={triggerRef}
                type="button"
                data-testid={testId}
                aria-label={ariaLabel}
                aria-haspopup="menu"
                aria-expanded={open}
                disabled={items.length === 0}
                onClick={() => setOpen(v => !v)}
                className="p-xs rounded-full text-icon-secondary transition-colors enabled:cursor-pointer enabled:hover:bg-overlay-medium disabled:text-icon-tertiary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring"
            >
                <IconMoreVertical />
            </button>
            {open && (
                <div
                    ref={menuRef}
                    role="menu"
                    aria-label={ariaLabel}
                    onKeyDown={handleMenuKeyDown}
                    className="absolute right-0 top-full z-40 mt-xs min-w-48 py-xs rounded-lg shadow-medium ring-1 ring-line-subtle bg-surface-elevated"
                >
                    {items.map(item => (
                        <button
                            key={item.label}
                            type="button"
                            role="menuitem"
                            tabIndex={-1}
                            data-testid={item.testId}
                            onClick={() => { triggerRef.current?.focus(); setOpen(false); item.onSelect(); }}
                            className="w-full px-l py-s text-left text-m text-content whitespace-nowrap cursor-pointer transition-colors hover:bg-overlay-weak focus-visible:outline-none focus-visible:bg-overlay-weak"
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
